<template>
  <div class="mines-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-mines" :size="18" /> {{ $t('games.mines.title') }}
        <span class="game-tag">{{ $t('games.mines.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ active: soundEnabled }"
          :title="$t('games.toggle_sound')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas Area -->
      <div class="game-canvas-area">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <!-- Result Overlay -->
        <div class="result-overlay" :class="{ active: gamePhase === 'gameover' || gamePhase === 'cashed_out' }">
          <template v-if="gamePhase === 'gameover'">
            <div class="result-text loss"><AppIcon name="bust" :size="24" /> {{ $t('games.mines.boom') }}</div>
            <div class="result-amount loss">-${{ betAmount.toFixed(2) }}</div>
          </template>
          <template v-else-if="gamePhase === 'cashed_out'">
            <div class="result-text win"><AppIcon name="cashout" :size="24" /> {{ $t('games.cashed_out') }}</div>
            <div class="result-amount win">+${{ (lastPayout - betAmount).toFixed(2) }}</div>
            <div class="result-mult">{{ lastMultiplier.toFixed(4) }}×</div>
          </template>
          <button class="btn-new-game" @click="resetGame">{{ $t('games.mines.new_game') }}</button>
        </div>

        <!-- Mines Grid -->
        <div class="mines-grid-wrap">
          <div class="mines-grid">
            <button
              v-for="tile in tiles"
              :key="tile.index"
              class="tile"
              :class="tileClass(tile)"
              :disabled="!canClick(tile)"
              @click="revealTile(tile.index)"
            >
              <span class="tile-face tile-back">
                <span class="tile-question">◆</span>
              </span>
              <span class="tile-face tile-front">
                <span v-if="tile.state === 'safe'" class="tile-icon"><AppIcon name="safe-gem" :size="20" /></span>
                <span v-else-if="tile.state === 'mine'" class="tile-icon"><AppIcon name="game-mines" :size="20" /></span>
              </span>
            </button>
          </div>
        </div>

        <!-- Live Multiplier Badge (during active game) -->
        <div v-if="gamePhase === 'active' && safeRevealed > 0" class="multiplier-badge">
          <span class="mult-label">{{ $t('games.multiplier') }}</span>
          <span class="mult-value">{{ currentMultiplier.toFixed(4) }}×</span>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button class="game-tab active">{{ $t('games.bet_tab') }}</button>
          <button class="game-tab" @click="router.push({ name: 'player.provably-fair' })">{{ $t('games.rules_tab') }}</button>
        </div>

        <div class="game-tab-content">
          <!-- Bet Amount -->
          <label class="ctrl-label">{{ $t('games.bet_amount') }}</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              class="bet-input"
              type="number"
              v-model.number="betAmount"
              min="0.01"
              step="0.01"
              :disabled="gamePhase === 'active'"
            />
          </div>
          <div class="bet-shortcuts">
            <button class="bet-short" :disabled="gamePhase === 'active'" @click="halveBet">½</button>
            <button class="bet-short" :disabled="gamePhase === 'active'" @click="doubleBet">2×</button>
            <button class="bet-short" :disabled="gamePhase === 'active'" @click="maxBet">Max</button>
          </div>

          <!-- Mine Count Selector -->
          <label class="ctrl-label">{{ $t('games.mines.mine_count_label') }}</label>
          <div class="mine-count-control">
            <button
              class="mine-adj-btn"
              :disabled="gamePhase === 'active'"
              @click="adjustMines(-1)"
            >
              −
            </button>
            <input
              class="mine-count-input"
              type="number"
              v-model.number="mineCount"
              min="1"
              max="24"
              :disabled="gamePhase === 'active'"
              @change="clampMines"
            />
            <button
              class="mine-adj-btn"
              :disabled="gamePhase === 'active'"
              @click="adjustMines(1)"
            >
              +
            </button>
          </div>

          <!-- Stats -->
          <div class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.mines.current_multiplier') }}</div>
              <div class="sp-value gold">{{ currentMultiplier.toFixed(4) }}×</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.mines.next_multiplier') }}</div>
              <div class="sp-value">{{ nextMultiplier.toFixed(4) }}×</div>
            </div>
          </div>

          <div class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.mines.safe_chance') }}</div>
              <div class="sp-value">{{ nextSafeChance.toFixed(1) }}%</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.payout') }}</div>
              <div class="sp-value gold">${{ currentPayout.toFixed(2) }}</div>
            </div>
          </div>

          <!-- Action Buttons -->
          <button
            v-if="gamePhase !== 'active'"
            class="btn-action"
            :disabled="loading"
            @click="startGame"
          >
            {{ $t('games.place_bet') }}
          </button>

          <button
            v-if="gamePhase === 'active'"
            class="btn-cashout"
            :disabled="safeRevealed < 1 || loading"
            @click="cashout"
          >
            {{ loading ? $t('games.cashing_out') : $t('games.mines.cash_out_button', { amount: currentPayout.toFixed(2) }) }}
          </button>

          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'

const GRID_SIZE = 25
const HOUSE_EDGE = 0.01

type TileState = 'hidden' | 'safe' | 'mine'
type GamePhase = 'idle' | 'active' | 'gameover' | 'cashed_out'

interface Tile {
  index: number
  state: TileState
  revealing: boolean
}

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toastStore = useToastStore()

// --- Game state ---
const gamePhase = ref<GamePhase>('idle')
const gameStateId = ref<number | null>(null)
const tiles = ref<Tile[]>(buildTiles())
const betAmount = ref(1.0)
const mineCount = ref(3)
const currentMultiplier = ref(0.0)
const safeRevealed = ref(0)
const lastPayout = ref(0)
const lastMultiplier = ref(0)
const loading = ref(false)
const soundEnabled = ref(true)

// --- Computed ---
const nextMultiplier = computed(() => calcMultiplier(safeRevealed.value + 1, mineCount.value))

const nextSafeChance = computed(() => {
  const tilesLeft = GRID_SIZE - safeRevealed.value
  if (tilesLeft <= 0) return 0
  const safeLeft = GRID_SIZE - mineCount.value - safeRevealed.value
  return Math.max(0, (safeLeft / tilesLeft) * 100)
})

const currentPayout = computed(() => {
  if (safeRevealed.value < 1) return 0
  return betAmount.value * currentMultiplier.value
})

const formattedBalance = computed(() => {
  const bal = walletStore.balance?.balance
  if (bal == null) return '—'
  return `$${bal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
})

// --- Helpers ---
function buildTiles(): Tile[] {
  return Array.from({ length: GRID_SIZE }, (_, i) => ({
    index: i,
    state: 'hidden' as TileState,
    revealing: false,
  }))
}

function calcMultiplier(k: number, mines: number): number {
  const n = GRID_SIZE
  const m = mines
  if (k <= 0 || m >= n || n - m < k) return 0
  let prob = 1
  for (let i = 0; i < k; i++) {
    prob *= (n - m - i) / (n - i)
  }
  return (1 / prob) * (1 - HOUSE_EDGE)
}

function tileClass(tile: Tile): Record<string, boolean> {
  return {
    safe: tile.state === 'safe',
    mine: tile.state === 'mine',
    flipped: tile.state !== 'hidden',
    revealing: tile.revealing,
    'tile-inactive': gamePhase.value !== 'active' || tile.state !== 'hidden',
  }
}

function canClick(tile: Tile): boolean {
  return gamePhase.value === 'active' && tile.state === 'hidden' && !loading.value
}

function halveBet() {
  betAmount.value = Math.max(0.01, +(betAmount.value / 2).toFixed(2))
}

function doubleBet() {
  betAmount.value = +(betAmount.value * 2).toFixed(2)
}

function maxBet() {
  const bal = walletStore.balance?.balance
  if (bal != null && bal > 0) {
    betAmount.value = +bal.toFixed(2)
  }
}

function adjustMines(delta: number) {
  mineCount.value = Math.max(1, Math.min(24, mineCount.value + delta))
}

function clampMines() {
  mineCount.value = Math.max(1, Math.min(24, Math.round(mineCount.value)))
}

function resetGame() {
  tiles.value = buildTiles()
  gamePhase.value = 'idle'
  gameStateId.value = null
  currentMultiplier.value = 0
  safeRevealed.value = 0
  lastPayout.value = 0
  lastMultiplier.value = 0
}

// --- API call types ---
interface StartResponse {
  game_state_id: number
  mine_count: number
  grid_size: number
  current_multiplier: number
  safe_revealed_count: number
  revealed_tiles: number[]
}

interface RevealResponse {
  is_mine: boolean
  tile_index: number
  revealed_tiles: number[]
  mine_positions: number[] | null
  safe_revealed_count: number
  current_multiplier: number
  next_multiplier: number
  next_safe_chance: number
  status: string
}

interface CashoutResponse {
  payout: number
  multiplier: number
  mine_positions: number[]
  safe_revealed_count: number
  status: string
}

// --- Start Game ---
async function startGame() {
  if (loading.value) return

  if (!betAmount.value || betAmount.value <= 0) {
    toastStore.error(t('games.invalid_bet_amount'))
    return
  }
  const bal = walletStore.balance?.balance ?? 0
  if (betAmount.value > bal) {
    toastStore.error(t('games.insufficient_balance'))
    return
  }

  loading.value = true
  try {
    const res = await apiClient.post<StartResponse>('/api/games/mines/start', {
      amount: betAmount.value.toFixed(2),
      mine_count: mineCount.value,
    })

    gameStateId.value = res.data.game_state_id
    gamePhase.value = 'active'
    tiles.value = buildTiles()
    currentMultiplier.value = 0
    safeRevealed.value = 0

    walletStore.applyGameResult(betAmount.value, 0)
  } catch (err: unknown) {
    const axiosError = err as { response?: { data?: { message?: string } } }
    toastStore.error(axiosError?.response?.data?.message ?? 'Failed to start game. Please try again.')
  } finally {
    loading.value = false
  }
}

// --- Reveal Tile ---
async function revealTile(index: number) {
  if (!canClick(tiles.value[index])) return

  // Optimistically mark as revealing (for animation)
  tiles.value[index].revealing = true
  loading.value = true

  try {
    const res = await apiClient.post<RevealResponse>('/api/games/mines/reveal', {
      tile_index: index,
    })

    const { is_mine, tile_index, revealed_tiles, mine_positions, safe_revealed_count, current_multiplier } = res.data

    if (is_mine) {
      // Reveal all mines immediately
      if (mine_positions) {
        for (const mineIdx of mine_positions) {
          tiles.value[mineIdx].state = 'mine'
          tiles.value[mineIdx].revealing = false
        }
      }
      // Mark the hit tile explicitly (may already be set above)
      tiles.value[tile_index].state = 'mine'
      tiles.value[tile_index].revealing = false

      currentMultiplier.value = 0
      safeRevealed.value = safe_revealed_count
      gamePhase.value = 'gameover'
    } else {
      // Safe tile
      tiles.value[tile_index].state = 'safe'
      tiles.value[tile_index].revealing = false

      currentMultiplier.value = current_multiplier
      safeRevealed.value = safe_revealed_count
    }

    // Sync any other previously revealed tiles
    for (const idx of revealed_tiles) {
      if (tiles.value[idx].state === 'hidden') {
        tiles.value[idx].state = is_mine && mine_positions?.includes(idx) ? 'mine' : 'safe'
        tiles.value[idx].revealing = false
      }
    }
  } catch (err: unknown) {
    tiles.value[index].revealing = false
    const axiosError = err as { response?: { data?: { message?: string } } }
    toastStore.error(axiosError?.response?.data?.message ?? 'Failed to reveal tile. Please try again.')
  } finally {
    loading.value = false
  }
}

// --- Cashout ---
async function cashout() {
  if (loading.value || safeRevealed.value < 1) return

  loading.value = true
  try {
    const res = await apiClient.post<CashoutResponse>('/api/games/mines/cashout', {})

    const { payout, multiplier, mine_positions } = res.data

    // Reveal all mines
    for (const mineIdx of mine_positions) {
      tiles.value[mineIdx].state = 'mine'
    }

    lastPayout.value = payout
    lastMultiplier.value = multiplier
    gamePhase.value = 'cashed_out'

    walletStore.applyGameResult(0, payout)

    toastStore.success(`Cashed out $${payout.toFixed(2)} at ${multiplier.toFixed(4)}×`)
  } catch (err: unknown) {
    const axiosError = err as { response?: { data?: { message?: string } } }
    toastStore.error(axiosError?.response?.data?.message ?? 'Cashout failed. Please try again.')
  } finally {
    loading.value = false
  }
}

// --- Resume game on mount ---
interface StateResponse {
  status: string
  game_state_id?: number
  mine_count?: number
  current_multiplier?: number
  next_multiplier?: number
  next_safe_chance?: number
  safe_revealed_count?: number
  revealed_tiles?: number[]
  bet_amount?: number
}

onMounted(async () => {
  if (!walletStore.balance) {
    walletStore.fetchBalance()
  }

  // Check for a pending Mines game (page refresh recovery)
  try {
    const res = await apiClient.get<StateResponse>('/api/games/mines/state')
    const data = res.data

    if (data.status === 'pending' && data.game_state_id) {
      gameStateId.value = data.game_state_id
      mineCount.value = data.mine_count ?? 3
      currentMultiplier.value = data.current_multiplier ?? 0
      safeRevealed.value = data.safe_revealed_count ?? 0
      betAmount.value = data.bet_amount ?? 1.0
      gamePhase.value = 'active'

      // Restore revealed safe tiles (mine positions still hidden)
      const revealed = data.revealed_tiles ?? []
      for (const idx of revealed) {
        tiles.value[idx].state = 'safe'
      }
    }
  } catch {
    // Silently ignore — no pending game
  }
})
</script>

<style scoped>
/* ---- Layout ---- */
.mines-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  animation: fadeIn 0.25s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ---- Topbar ---- */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-back:hover {
  color: var(--color-on-dark, #fff);
  background: var(--bg-hover, #2a4455);
}

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  border: none;
}
.icon-btn:hover, .icon-btn.active {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
}

/* ---- Game body ---- */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* ---- Canvas area ---- */
.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.canvas-bg-glow {
  position: absolute;
  pointer-events: none;
  right: -100px;
  top: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 231, 1, 0.05) 0%, transparent 65%);
}

/* ---- Result Overlay ---- */
.result-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(13, 27, 42, 0.88);
  backdrop-filter: blur(4px);
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  gap: 8px;
}
.result-overlay.active {
  opacity: 1;
  pointer-events: all;
}

.result-text {
  font-family: 'Russo One', sans-serif;
  font-size: 40px;
  animation: winPop 0.4s ease both;
}
.result-text.win { color: var(--color-success, #00e701); text-shadow: 0 0 40px rgba(0, 231, 1, 0.5); }
.result-text.loss { color: var(--color-danger, #ed4245); text-shadow: 0 0 40px rgba(237, 66, 69, 0.5); }

.result-amount {
  font-family: 'JetBrains Mono', monospace;
  font-size: 24px;
  font-weight: 700;
}
.result-amount.win { color: var(--color-success, #00e701); }
.result-amount.loss { color: var(--color-danger, #ed4245); }

.result-mult {
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px;
  color: var(--accent-gold, #ffd700);
  font-weight: 700;
}

.btn-new-game {
  margin-top: 16px;
  padding: 12px 28px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-new-game:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.4);
  transform: translateY(-1px);
}

@keyframes winPop {
  0% { transform: scale(0.7); opacity: 0; }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); opacity: 1; }
}

/* ---- Mines Grid ---- */
.mines-grid-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.mines-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  width: min(420px, 90vw);
}

/* ---- Tile ---- */
.tile {
  aspect-ratio: 1;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--bg-tertiary, #213743);
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.35s ease, border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  font-size: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.tile:hover:not(:disabled):not(.tile-inactive) {
  background: var(--bg-hover, #2a4455);
  border-color: rgba(255, 255, 255, 0.25);
  transform: scale(1.06);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

/* Flip faces */
.tile-face {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 10px;
}

.tile-back {
  transform: rotateY(0deg);
}

.tile-front {
  transform: rotateY(180deg);
}

/* Flipped state */
.tile.flipped {
  transform: rotateY(180deg);
}

.tile.flipped:hover:not(:disabled) {
  transform: rotateY(180deg) scale(1.0);
  cursor: default;
}

/* Safe tile */
.tile.safe .tile-front {
  background: rgba(0, 231, 1, 0.15);
  border: 1px solid rgba(0, 231, 1, 0.4);
}
.tile.safe {
  border-color: rgba(0, 231, 1, 0.4);
  box-shadow: 0 0 10px rgba(0, 231, 1, 0.15);
}

/* Mine tile */
.tile.mine .tile-front {
  background: rgba(237, 66, 69, 0.15);
  border: 1px solid rgba(237, 66, 69, 0.4);
}
.tile.mine {
  border-color: rgba(237, 66, 69, 0.4);
  box-shadow: 0 0 10px rgba(237, 66, 69, 0.2);
}

/* Disabled/inactive state */
.tile-inactive {
  cursor: not-allowed;
  opacity: 0.7;
}

.tile:disabled {
  cursor: not-allowed;
}

/* Question mark icon */
.tile-question {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.2);
}

.tile-icon {
  font-size: 26px;
  line-height: 1;
}

/* Revealing animation */
.tile.revealing {
  animation: tileReveal 0.2s ease;
}

@keyframes tileReveal {
  0% { transform: scale(0.92); }
  60% { transform: scale(1.08) rotateY(90deg); }
  100% { transform: rotateY(180deg); }
}

/* ---- Multiplier Badge ---- */
.multiplier-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 99px;
  padding: 6px 16px;
  backdrop-filter: blur(6px);
  z-index: 1;
  animation: fadeIn 0.3s ease;
}

.mult-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
}

.mult-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  font-weight: 800;
  color: var(--accent-gold, #ffd700);
  text-shadow: 0 0 12px rgba(255, 215, 0, 0.4);
}

/* ---- Sidebar ---- */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}
.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}
.game-tab:hover:not(.active) {
  color: var(--text-secondary, #b1bad3);
}

.game-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.game-tab-content::-webkit-scrollbar { width: 3px; }

/* ---- Controls ---- */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}

.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}

.bet-input {
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 10px 10px 24px;
  color: var(--text-primary, #fff);
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: border-color 150ms ease;
}
.bet-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}
.bet-input:disabled { opacity: 0.5; }

.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;
  text-align: center;
  font-family: inherit;
}
.bet-short:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}
.bet-short:disabled { opacity: 0.4; cursor: not-allowed; }

/* Mine count control */
.mine-count-control {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.mine-adj-btn {
  width: 36px;
  height: 36px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary, #fff);
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease;
  flex-shrink: 0;
  font-family: inherit;
}
.mine-adj-btn:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  border-color: rgba(255, 255, 255, 0.2);
}
.mine-adj-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.mine-count-input {
  flex: 1;
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px;
  color: var(--accent-primary, #00e701);
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px;
  font-weight: 800;
  text-align: center;
  transition: border-color 150ms ease;
}
.mine-count-input:focus { outline: none; border-color: rgba(0, 231, 1, 0.4); }
.mine-count-input:disabled { opacity: 0.5; }

/* Stat pills */
.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 10px;
}

.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}

.sp-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}
.sp-value.gold { color: var(--accent-gold, #ffd700); }

/* Buttons */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}
.btn-action:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-action:active:not(:disabled) { transform: translateY(0); }

.btn-cashout {
  width: 100%;
  padding: 14px;
  background: var(--color-warning, #ffa726);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-cashout:hover:not(:disabled) {
  background: var(--color-warning, #e09000);
  box-shadow: 0 0 20px rgba(255, 167, 38, 0.4);
  transform: translateY(-1px);
}
.btn-cashout:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-cashout:active:not(:disabled) { transform: translateY(0); }

/* Balance row */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
}

.bal-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.bal-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}
.bal-value.green { color: var(--color-success, #00e701); }

/* ---- Responsive ---- */
@media (max-width: 900px) {
  .game-sidebar { width: 240px; }
}

@media (max-width: 768px) {
  .game-body { flex-direction: column; }
  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 340px;
  }
  .game-canvas-area { padding: 16px; }
  .mines-grid { width: min(360px, 94vw); gap: 6px; }
}
</style>
